// src/pages/Coleta/components/IndicadoresDialog.jsx
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Link,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import dayjs from 'dayjs';
import { formatarValorLab, parseValorLab, valorParaTexto } from '../../../utils/numeroLab';
import { mensagemErro } from '../../../infra/api/Coletas';

const MAX_METAS = 10;
const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

let proximoId = 0;
const novoId = () => `nova-${Date.now()}-${proximoId++}`;

const paraRascunho = (referencias) => ({
    legislacao: (referencias?.itens || []).filter((i) => i.tipo === 'LEGISLACAO').map((i) => ({ ...i })),
    metas: (referencias?.itens || [])
        .filter((i) => i.tipo === 'META_INTERNA')
        .map((i) => ({ ...i, texto: valorParaTexto(i.valor), descricao: i.descricao || '' })),
    principal: (referencias?.itens || []).find((i) => i.principal)?.id || '',
});

const metaInvalida = (m) => !m.nome.trim() || !(parseValorLab(m.texto) > 0);

function Secao({ titulo, subtitulo, children }) {
    return (
        <Box>
            <Typography fontWeight={700}>{titulo}</Typography>
            {subtitulo && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitulo}</Typography>
            )}
            {children}
        </Box>
    );
}

/**
 * "Legislação e indicadores": as linhas de referência do gráfico de evolução e o limite
 * que o assistente usa nos alertas. A legislação vem da norma (só se escolhe exibir); as
 * metas da propriedade são da conta. Tudo vale para todos os usuários — por isso só
 * ADMIN e GESTOR alteram; os demais veem as mesmas informações sem editar.
 */
export default function IndicadoresDialog({ open, onClose, referencias, podeEditar, onSalvar }) {
    const [rascunho, setRascunho] = useState(() => paraRascunho(referencias));
    const [tentouSalvar, setTentouSalvar] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (open) {
            setRascunho(paraRascunho(referencias));
            setTentouSalvar(false);
            setErro('');
        }
    }, [open, referencias]);

    const unidade = referencias?.unidade || '';
    const alterarLei = (id, exibir) =>
        setRascunho((r) => ({ ...r, legislacao: r.legislacao.map((l) => (l.id === id ? { ...l, exibir } : l)) }));
    const alterarMeta = (id, campo, valor) =>
        setRascunho((r) => ({ ...r, metas: r.metas.map((m) => (m.id === id ? { ...m, [campo]: valor } : m)) }));
    const removerMeta = (id) =>
        setRascunho((r) => ({ ...r, metas: r.metas.filter((m) => m.id !== id), principal: r.principal === id ? '' : r.principal }));
    const adicionarMeta = () =>
        setRascunho((r) => ({ ...r, metas: [...r.metas, { id: novoId(), nome: '', texto: '', descricao: '', exibir: true }] }));

    const opcoesAssistente = [
        ...rascunho.legislacao.map((l) => ({ id: l.id, rotulo: `${l.nome} · ${INTEIRO.format(l.valor)} ${unidade}` })),
        ...rascunho.metas
            .filter((m) => m.nome.trim() && parseValorLab(m.texto) > 0)
            .map((m) => ({ id: m.id, rotulo: `${m.nome.trim()} · ${INTEIRO.format(parseValorLab(m.texto))} ${unidade}` })),
    ];

    const salvar = async () => {
        setTentouSalvar(true);
        if (rascunho.metas.some(metaInvalida)) return;
        setSalvando(true);
        setErro('');
        try {
            await onSalvar({
                itens: [
                    ...rascunho.legislacao.map((l) => ({ id: l.id, tipo: 'LEGISLACAO', exibir: l.exibir })),
                    ...rascunho.metas.map((m) => ({
                        id: m.id,
                        tipo: 'META_INTERNA',
                        nome: m.nome.trim(),
                        valor: parseValorLab(m.texto),
                        descricao: m.descricao.trim() || null,
                        exibir: m.exibir,
                    })),
                ],
                principal: rascunho.principal || null,
            });
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível salvar as referências.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
                <Typography component="span" variant="h6" fontWeight={700} display="block">
                    Legislação e indicadores
                </Typography>
                <Typography component="span" variant="body2" color="text.secondary" display="block">
                    Linhas de referência do gráfico e o limite usado pelo assistente.
                </Typography>
                <IconButton onClick={onClose} disabled={salvando} aria-label="Fechar" sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <Lucide.X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} divider={<Divider flexItem />} sx={{ mt: 2 }}>
                    <Secao titulo="Legislação" subtitulo={`Valores da norma, em ${unidade}. Não são editáveis.`}>
                        <Stack spacing={1.5}>
                            {rascunho.legislacao.length === 0 && (
                                <Typography variant="body2" color="text.secondary">Sem referência legal cadastrada para esta página.</Typography>
                            )}
                            {rascunho.legislacao.map((l) => (
                                <Box key={l.id} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'baseline' }} spacing={{ xs: 0, sm: 1 }}>
                                        <Typography fontWeight={700}>{l.nome}</Typography>
                                        <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                                            {INTEIRO.format(l.valor)} {unidade}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        {l.fonte}. {l.descricao}
                                    </Typography>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={l.exibir} disabled={!podeEditar}
                                                onChange={(e) => alterarLei(l.id, e.target.checked)} />}
                                            label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Exibir no gráfico</Typography>}
                                        />
                                        {l.link && (
                                            <Link href={l.link} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                                Consultar norma
                                            </Link>
                                        )}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </Secao>

                    <Secao
                        titulo="Metas da propriedade"
                        subtitulo="Limites próprios, como a exigência do laticínio ou do programa de qualidade."
                    >
                        <Stack spacing={2}>
                            {rascunho.metas.length === 0 && (
                                <Typography variant="body2" color="text.secondary">Nenhuma meta cadastrada.</Typography>
                            )}
                            {rascunho.metas.map((m) => {
                                const erroNome = tentouSalvar && !m.nome.trim();
                                const erroValor = tentouSalvar && !(parseValorLab(m.texto) > 0);
                                return podeEditar ? (
                                    <Box key={m.id}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1.5 }}>
                                            <TextField label="Nome da meta" value={m.nome} fullWidth
                                                inputProps={{ maxLength: 60 }}
                                                onChange={(e) => alterarMeta(m.id, 'nome', e.target.value)}
                                                error={erroNome} helperText={erroNome ? 'Informe o nome.' : ' '} />
                                            <TextField label={unidade} value={m.texto}
                                                onChange={(e) => alterarMeta(m.id, 'texto', formatarValorLab(e.target.value))}
                                                inputProps={{ inputMode: 'decimal' }}
                                                error={erroValor} helperText={erroValor ? 'Valor maior que zero.' : ' '}
                                                sx={{ width: { sm: 180 }, flexShrink: 0 }} />
                                        </Stack>
                                        <TextField label="Observação (opcional)" value={m.descricao} fullWidth size="small"
                                            inputProps={{ maxLength: 280 }}
                                            onChange={(e) => alterarMeta(m.id, 'descricao', e.target.value)} />
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <FormControlLabel
                                                control={<Checkbox size="small" checked={m.exibir}
                                                    onChange={(e) => alterarMeta(m.id, 'exibir', e.target.checked)} />}
                                                label={<Typography variant="body2">Exibir no gráfico</Typography>}
                                            />
                                            <Button size="small" onClick={() => removerMeta(m.id)} sx={{ fontWeight: 600 }}>Remover</Button>
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Box key={m.id}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                            <Typography fontWeight={700}>{m.nome}</Typography>
                                            <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{INTEIRO.format(m.valor)} {unidade}</Typography>
                                        </Stack>
                                        {m.descricao && <Typography variant="caption" color="text.secondary">{m.descricao}</Typography>}
                                    </Box>
                                );
                            })}
                            {podeEditar && rascunho.metas.length < MAX_METAS && (
                                <Button variant="outlined" color="inherit" startIcon={<Lucide.Plus size={16} />} onClick={adicionarMeta}
                                    sx={{ py: 1.25, borderRadius: 2, borderColor: 'divider', fontWeight: 600 }}>
                                    Adicionar meta
                                </Button>
                            )}
                        </Stack>
                    </Secao>

                    <Secao titulo="Alertas do assistente" subtitulo="O assistente avisa quando uma coleta passa desta referência.">
                        <TextField select fullWidth size="small" label="Referência dos alertas" value={rascunho.principal}
                            disabled={!podeEditar}
                            sx={{ '& .MuiSelect-select.MuiSelect-select': { whiteSpace: 'normal' } }}
                            onChange={(e) => setRascunho((r) => ({ ...r, principal: e.target.value }))}>
                            {opcoesAssistente.map((o) => (
                                <MenuItem key={o.id} value={o.id}>{o.rotulo}</MenuItem>
                            ))}
                            <MenuItem value="">Nenhuma: o assistente não compara com referência</MenuItem>
                        </TextField>
                    </Secao>
                </Stack>

                {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                    {podeEditar
                        ? 'As escolhas valem para todos os usuários da conta.'
                        : 'Somente o gestor da conta altera estas referências.'}
                    {referencias?.atualizadoEm &&
                        ` Última alteração: ${dayjs(referencias.atualizadoEm).format('DD/MM/YYYY HH:mm')}` +
                            (referencias.atualizadoPor ? ` por ${referencias.atualizadoPor}.` : '.')}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={salvando}>{podeEditar ? 'Cancelar' : 'Fechar'}</Button>
                {podeEditar && (
                    <Button variant="contained" onClick={salvar} disabled={salvando} disableElevation sx={{ borderRadius: 2 }}>
                        {salvando ? 'Salvando…' : 'Salvar'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
